import React from "react";
import { NumericFormat } from "react-number-format";
import makeStyles from "@mui/styles/makeStyles";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardMedia from "@mui/material/CardMedia";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Chip } from "@mui/material";
import {
  AssignmentTurnedIn,
  AssignmentLate,
  Assignment,
  FormatQuote,
  Today,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  nl2br,
  formatDate,
  authHeader,
  getStorageImagesForStandardOption,
  getStorageImagesForIndividualOption,
  setStorageImagesForStandardOption,
  setStorageImagesForIndividualOption,
} from "../../_helpers";
import { useSelector } from "react-redux";

const { webApiUrl } = window.appConfig;

const useStyles = makeStyles((theme) => ({
  card: {
    boxShadow: theme.shadows[0],
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.grey[200],
    margin: theme.spacing(1, -2),
  },
  fullWidth: { width: "100%" },
  mainContent: {
    padding: theme.spacing(2),
  },
  bottomContent: {
    padding: theme.spacing(0, 2, 2),
    width:"100%"
  },
  actionButton: {
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.grey[200],
    padding: 6,
    display: "inline-block",
    float: "right",
  },
  cardActionArea: {
    "&:focus": {
      outline: "none",
    },
  },
  bold: {
    fontWeight: "bold",
  },
  price: {
    color: "#ff8000",
    fontWeight: "bold",
  },

  media: {
    width: 200,
    padding: "35% 0",
    backgroundSize: "contain",
    [theme.breakpoints.down("sm")]: {
      width: 100,
      position: "relative",
      top: "calc(50% - 35px)",
    },
  },
  areaCollapse: {
    backgroundColor: theme.palette.grey[100],
  },
  numberFormat: {
    width: 160,
    margin: 0,
    "& input": {
      textAlign: "center",
      fontWeight: "bold",
      color: theme.palette.primary.main,
    },
  },
  numberFormatDecimal: {
    width: 190,
    margin: 0,
    "& input": {
      textAlign: "center",
      fontWeight: "bold",
      color: theme.palette.primary.main,
    },
  },
  popoverTypography: {
    padding: theme.spacing(2),
  },
}));

export default function SelectedOptionCard(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { option, showExclTax, ...others } = props;
  const [images, setImages] = React.useState(
    !!option.optionStandardId
      ? getStorageImagesForStandardOption(option.optionStandardId)
      : getStorageImagesForIndividualOption(option.optionId)
  );
  const [isImageListLoaded, setIsImageListLoaded] = React.useState(false);

  const { currencySymbol } = useSelector((state) => state.appConfig) || {}

  if (!isImageListLoaded) {
    if (option.optionStandardId) {
      const url =
        webApiUrl +
        "api/shopping/GetStandardOptionImageList/" +
        encodeURI(option.optionStandardId);
      const requestOptions = {
        method: "GET",
        headers: authHeader(),
      };

      fetch(url, requestOptions)
        .then((Response) => Response.json())
        .then((findResponse) => {
          setImages(findResponse);
          setIsImageListLoaded(true);
          setStorageImagesForStandardOption(
            option.optionStandardId,
            findResponse
          );
        });
    } else {
      const url =
        webApiUrl +
        "api/shopping/GetSelectedOptionImageList/" +
        encodeURI(option.optionId);
      const requestOptions = {
        method: "GET",
        headers: authHeader(),
      };

      fetch(url, requestOptions)
        .then((Response) => Response.json())
        .then((findResponse) => {
          setImages(findResponse);
          setIsImageListLoaded(true);
          setStorageImagesForIndividualOption(option.optionId, findResponse);
        });
    }
  }

  const priceFieldName = !showExclTax ? "salesPriceInclVAT" : "salesPriceExclVAT";

  const statusText =
    option.optionStatus === 3 ? (
      <Chip
        icon={<AssignmentTurnedIn />}
        label={t("layout.menuitem.housingwishes.finaloptions")}
        className={classes.chip}
        variant="outlined"
        color="primary"
        size="small"
      />
    ) : option.optionStatus === 5 ? (
      <Chip
        icon={<AssignmentLate />}
        label={t("buyersGuide.options.orderedOnlineAndUnsigned")}
        className={classes.chip}
        variant="outlined"
        color="secondary"
        size="small"
      />
    ) : option.optionStatus === 6 ? (
      <Chip
        icon={<Assignment />}
        label={t("buyersGuide.options.orderedOnline")}
        className={classes.chip}
        variant="outlined"
        size="small"
      />
    ) : (
      ""
    );

  return (
    (<Card className={classes.card} {...others} ref={option.ref}>
      <CardActionArea className={classes.cardActionArea} disabled>
        <Grid container >
          <Grid container spacing={2} className={classes.mainContent} flexGrow={1}>
            <Grid container direction="column" size="grow">
              <Grid size="grow">
                <Typography variant="body1" className={classes.bold}>
                  {option.optionNo + " - " + option.description}
                </Typography>
                <br />
                <Typography variant="body2" sx={{color:"textSecondary"}} >
                  {nl2br(option.commercialDescription)}
                </Typography>
                              {
                                  option.additionalDescription && option.additionalDescription.trim() !== "" &&
                                  <>
                                      <br />
                                      <Typography variant="body2" component="div" sx={{
                                          color: "text.secondary"
                                      }}>
                                          <FormatQuote fontSize="small" />
                                          {option.additionalDescription}
                                      </Typography>
                                  </>
                              }
                <br />
              </Grid>
            </Grid>
            {images && images.length > 0 && (
              <Grid>
                <CardMedia
                  className={classes.media}
                  image={
                    webApiUrl +
                    "api/home/GetAttachment/" +
                    encodeURI(images[0].id)
                  }
                  title={option.description}
                />
              </Grid>
            )}
          </Grid>
 

          <Grid
            container
            spacing={1}
            className={classes.bottomContent}
            sx={{
              justifyContent: "flex-end"
            }}>
            <Grid flexGrow={1}>
              <Typography variant="body1" className={classes.price} noWrap>
                {!option.salesPriceEstimated &&
                  !option.salesPriceToBeDetermined && (
                    <React.Fragment>{showExclTax === true && t('buyersGuide.standardOptions.salesPriceExclVAT.short')} {currencySymbol} </React.Fragment>
                  )}
                {option[`${priceFieldName}_Text`]}
                &nbsp;
                {option.unit && (
                  <React.Fragment>
                    {t("general.per")}
                    &nbsp;
                    {option.unit}
                  </React.Fragment>
                )}
              </Typography>
              {
                showExclTax && //when showExclTax is true then above the excl tax price is shown and under this condition the incl tax price is shown
                <Typography variant="body2" sx={{color:'text.secondary'}} noWrap>
                  {!option.salesPriceEstimated &&
                    !option.salesPriceToBeDetermined && (
                      <>{t('buyersGuide.standardOptions.salesPriceInclVAT.short')} {currencySymbol} </>
                    )}
                  {option.salesPriceInclVAT_Text}
                  &nbsp;
                  {option.unit && (
                    <React.Fragment>
                      {t("general.per")}
                      &nbsp;
                      {option.unit}
                    </React.Fragment>
                  )}
                </Typography>
              }
            </Grid>
            <Grid>
              <Chip
                icon={<Today />}
                label={formatDate(new Date(option.modifiedOn))}
                className={classes.chip}
                variant="outlined"
                size="small"
              />
            </Grid>
            {option.optionStatus !== 5 && <Grid>{statusText}</Grid>}
            <Grid>
              <Chip
                size="small"
                label={
                  <React.Fragment>
                    <NumericFormat
                      displayType="text"
                      decimalScale={option.decimalPlaces}
                      fixedDecimalScale={true}
                      thousandSeparator="."
                      decimalSeparator=","
                      value={option.quantity}
                      suffix="&nbsp;"
                    />
                    <span>{option.unit}&nbsp;{currencySymbol}&nbsp;</span>
                    <NumericFormat
                      displayType="text"
                      decimalScale={2}
                      fixedDecimalScale={true}
                      thousandSeparator="."
                      decimalSeparator=","
                      value={option.quantity * option[priceFieldName]}
                    />
                  </React.Fragment>
                }
                color="primary"
                className={classes.bold}
              />
            </Grid>
          </Grid>
        </Grid>
      </CardActionArea>
    
    </Card>)
  );
}
